import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import './AudioPlayer.css';

const BARS = [4, 8, 14, 7, 18, 11, 5, 20, 13, 8, 16, 6, 12, 10, 19, 7, 15, 5, 11, 9, 17, 6, 13, 10, 16, 7, 12, 5, 9, 14];

const formatTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const AudioPlayer = forwardRef(({ src, theme = 'dark' }, ref) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useImperativeHandle(ref, () => ({
    pause: () => {
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  }));

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoaded = () => setDuration(audio.duration);
    const onEnded = () => { setIsPlaying(false); setCurrentTime(0); };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  // Reset when src changes
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleBarClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    if (audioRef.current && duration) {
      audioRef.current.currentTime = ratio * duration;
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const displayTime = (isPlaying || currentTime > 0) ? formatTime(currentTime) : formatTime(duration);

  return (
    <div className={`audioPlayer audioPlayer--${theme}`}>
      <audio ref={audioRef} src={src} preload="metadata" />

      <button className="audioPlayBtn" onClick={togglePlay} aria-label={isPlaying ? 'Pausar' : 'Reproducir'}>
        {isPlaying ? (
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <polygon points="6,3 20,12 6,21" />
          </svg>
        )}
      </button>

      <div className="audioWaveform" onClick={handleBarClick} role="progressbar" aria-valuenow={Math.round(progress)}>
        {BARS.map((h, i) => {
          const barProgress = (i / BARS.length) * 100;
          return (
            <div
              key={i}
              className={`audioBar ${barProgress <= progress ? 'audioBar--played' : ''}`}
              style={{ height: `${h}px` }}
            />
          );
        })}
      </div>

      <span className="audioTime">{displayTime}</span>
    </div>
  );
});

AudioPlayer.displayName = 'AudioPlayer';
export default AudioPlayer;
